import i18n from 'i18next';
import type { BackendModule, ReadCallback, Services, InitOptions } from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import de from './locales/de.json';
import { DEFAULT_LOCALE, isSupportedLocale, localeMeta } from '@4m/shared';
import { setFormattingLocale } from '@/lib/formatLocale';

// German is bundled eagerly because it is `fallbackLng`: every key that a
// target locale has not translated yet resolves through it, so it must be
// present synchronously. Everything else loads on demand.
//
// This matters at 30 languages. Statically importing all of them would put
// ~5,8 MB of raw JSON into the entry chunk, behind the top-level await below —
// i.e. blocking first paint for a user who reads one language.
//
// import.meta.glob gives Vite a static list of candidates, so each locale is
// code-split into its own chunk and fetched only when selected.
//
// German stays in the glob on purpose. Vite sees it is also statically imported
// and hoists it into one shared chunk that the entry depends on — same bytes on
// the critical path either way (measured: 1441 kB vs 1439 kB), but as its own
// file it keeps its own hash. That matters while translating: de.json changes
// constantly and must not invalidate the entry chunk on every edit.
//
// Measured effect of this whole mechanism, critical path (entry + preloads):
//   3 locales statically imported   1752 kB raw / 516 kB gzip
//   only German on the critical path 1441 kB raw / 409 kB gzip
// At 30 locales the first number would be ~5,8 MB raw; the second does not move.
const loaders = import.meta.glob<{ default: Record<string, unknown> }>('./locales/*.json');

const lazyLocales: BackendModule = {
  type: 'backend',
  init(_services: Services, _backendOptions: unknown, _i18nextOptions: InitOptions) {},
  read(language: string, _namespace: string, callback: ReadCallback) {
    const loader = loaders[`./locales/${language}.json`];
    if (!loader) {
      // Declared in the registry as 'planned' but not translated yet. Report an
      // empty bundle rather than an error: i18next then falls through to
      // German, which is also what every t() call's inline default renders.
      callback(null, {});
      return;
    }
    loader()
      .then((mod) => callback(null, mod.default))
      .catch((err) => callback(err as Error, false));
  },
};

await i18n
  .use(lazyLocales)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // Only German ships in the entry chunk; partialBundledLanguages lets the
    // backend above supply the rest without i18next also re-fetching this one.
    resources: { de: { translation: de } },
    partialBundledLanguages: true,
    // NOTE: no explicit `lng` here — setting it disables LanguageDetector
    // entirely (i18next semantics), which silently reset every user back to
    // German on each reload and overwrote their stored choice. The detector
    // reads localStorage only; brand-new users (no stored choice) get the
    // German-first default via fallbackLng.
    fallbackLng: DEFAULT_LOCALE,
    // Keeps a stored or detected 'de-AT' resolving to the 'de' bundle. Every
    // registry code is bare by design, so this never truncates a real locale —
    // region and calendar live in LocaleMeta.intl, not in the resource code.
    load: 'languageOnly',
    cleanCode: true,
    debug: import.meta.env.DEV,
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },
    // Required: a locale now arrives after first paint. Without this, react-i18next
    // would suspend the whole tree on every language change.
    react: { useSuspense: false },
    ns: ['translation'],
    defaultNS: 'translation',
  });

// The direction last written to <html dir>. CSS reads the attribute; Radix
// cannot — it takes its direction from React context and falls back to 'ltr'
// (components/LocaleDirectionProvider.tsx). Both are fed from this one value so
// that the stylesheet and the primitives can never disagree about the page.
let activeDir: 'ltr' | 'rtl' = 'ltr';

/** Reading direction of the locale currently on screen. */
export function activeDirection(): 'ltr' | 'rtl' {
  return activeDir;
}

/**
 * Mirror the active locale onto <html>, which is where the browser reads text
 * direction, hyphenation and font fallback from. Kept in one place so the four
 * end-to-left languages get `dir` for free the moment they go live.
 */
function applyDocumentLocale(code: string): void {
  const meta = localeMeta(code) ?? localeMeta(DEFAULT_LOCALE)!;
  const root = document.documentElement;
  root.lang = meta.code;
  root.dir = meta.dir;
  activeDir = meta.dir;
  // Dates, times, numbers and prices resolve their Intl tag from here. Pushed
  // rather than pulled so lib/formatLocale never has to import this module —
  // that would make it untestable and create a cycle.
  setFormattingLocale(meta.code);
}

i18n.on('languageChanged', applyDocumentLocale);
applyDocumentLocale(i18n.resolvedLanguage ?? DEFAULT_LOCALE);

/**
 * Switch language. Prefer this over calling i18n.changeLanguage directly — it
 * refuses codes that are declared but not translated yet, so a user can never
 * be stranded on a fully German UI wearing a Korean label.
 */
export async function setLocale(code: string): Promise<void> {
  if (!isSupportedLocale(code)) return;
  await i18n.changeLanguage(code);
}

export default i18n;
